import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import * as React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { requireAuth } from "~/lib/auth-guard";
import { useResumeStore } from "~/stores/resume-store";
import { orpc } from "~/lib/orpc-client";
import { Skeleton, useToast } from "@alchema/ui";
import { ConfirmDialog } from "~/components/ConfirmDialog";
import { RouteErrorBoundary } from "~/components/RouteErrorBoundary";
import { ViewSwitcher } from "~/components/shared/ViewSwitcher";
import { TableIcon, CardIcon } from "~/components/shared/ViewIcons";
import { EmptyState } from "~/components/shared/EmptyState";
import * as m from "~/paraglide/messages";

export const Route = createFileRoute("/resumes/")({
  beforeLoad: async () => { await requireAuth(); },
  component: ResumesListPage,
  pendingComponent: ResumesListSkeleton,
  errorComponent: RouteErrorBoundary,
});

/* ── Relative time formatter ── */
function formatRelativeTime(date: string | Date | null | undefined): string {
  if (!date) return "\u2014";
  const d = new Date(date);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD}d ago`;
  return d.toLocaleDateString();
}

/* ── Skeleton ── */
function ResumesListSkeleton() {
  return (
    <div className="animate-fade-up">
      {/* Header skeleton */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <Skeleton className="h-7 w-28" />
          <Skeleton className="mt-1.5 h-4 w-20" />
        </div>
        <div className="flex items-center gap-3">
          <Skeleton className="h-8 w-16 rounded-lg" />
          <Skeleton className="h-9 w-28 rounded-lg" />
        </div>
      </div>
      {/* Table skeleton */}
      <div
        className="rounded-lg overflow-hidden"
        style={{ border: "1px solid var(--border-subtle)" }}
      >
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="flex items-center gap-4 px-4 py-3"
            style={{ borderBottom: i < 3 ? "1px solid var(--border-subtle)" : undefined }}
          >
            <Skeleton className="h-4 w-40 flex-1" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-6 w-14" />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Page ── */
function ResumesListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const store = useResumeStore();
  const { toast } = useToast();
  const [view, setView] = React.useState<"table" | "card">("table");

  const { data: orpcResumes, isError: orpcFailed } = useQuery({
    ...orpc.resume.list.queryOptions(),
    retry: 0,
  });
  const resumes = orpcFailed ? store.resumes : (orpcResumes ?? store.resumes);

  const deleteMutation = useMutation(orpc.resume.delete.mutationOptions({
    onSuccess: () => queryClient.invalidateQueries({ queryKey: orpc.resume.list.key({}) }),
    onError: () => { /* handled per-call */ },
  }));

  const updateMutation = useMutation(orpc.resume.update.mutationOptions({
    onSuccess: () => queryClient.invalidateQueries({ queryKey: orpc.resume.list.key({}) }),
    onError: () => { /* handled per-call */ },
  }));

  const [deleteTarget, setDeleteTarget] = React.useState<string | null>(null);

  function confirmDelete() {
    if (!deleteTarget) return;
    const id = deleteTarget;
    setDeleteTarget(null);
    if (orpcFailed) {
      store.deleteResume(id);
      toast(m.resumes_deleted(), "success");
    } else {
      deleteMutation.mutate({ id }, {
        onSuccess: () => toast(m.resumes_deleted(), "success"),
        onError: () => { store.deleteResume(id); toast(m.resumes_deleted(), "success"); },
      });
    }
  }

  function handleSetMaster(id: string) {
    if (orpcFailed) {
      store.setMaster(id);
      toast(m.resumes_set_master(), "success");
    } else {
      updateMutation.mutate({ id, isMaster: true }, {
        onSuccess: () => toast(m.resumes_set_master(), "success"),
        onError: () => { store.setMaster(id); toast(m.resumes_set_master(), "success"); },
      });
    }
  }

  function handleDuplicate(id: string) {
    const newId = store.duplicateResume(id);
    if (newId) {
      toast(m.resumes_duplicated());
      navigate({ to: "/resumes/$resumeId", params: { resumeId: newId } });
    }
  }

  return (
    <div className="animate-fade-up">
      {/* ── Attio page header ── */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1
            className="font-semibold"
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "var(--text-2xl, 1.5rem)",
              letterSpacing: "var(--tracking-snug, -0.015em)",
              color: "var(--text)",
            }}
          >
            {m.resumes_title()}
          </h1>
          <p className="mt-1 text-[13px]" style={{ color: "var(--text-muted)" }}>
            <span className="mono tabular">{resumes.length}</span> resume{resumes.length !== 1 ? "s" : ""}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <ViewSwitcher
            views={[
              { id: "table", icon: TableIcon, label: "Table view" },
              { id: "card", icon: CardIcon, label: "Card view" },
            ]}
            active={view}
            onChange={(v) => setView(v as "table" | "card")}
          />
          <Link to="/resumes/new" className="btn btn-primary btn-sm" data-testid="resume-create-btn">
            + {m.resumes_new_resume()}
          </Link>
        </div>
      </div>

      {/* ── Content ── */}
      {resumes.length === 0 ? (
        <EmptyState
          icon={
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
              <path d="M14 2v6h6" />
            </svg>
          }
          title={m.resumes_empty_title?.() ?? "No resumes yet"}
          description={m.resumes_empty_subtitle?.() ?? "Create your first resume to get started"}
          action={{ label: m.resumes_new_resume(), href: "/resumes/new" }}
        />
      ) : view === "table" ? (
        /* ── Table view ── */
        <div
          className="rounded-lg overflow-hidden"
          style={{ border: "1px solid var(--border-subtle)" }}
          data-testid="resume-list"
        >
          {/* Table header */}
          <div
            className="grid items-center px-4 py-2 text-[11px] font-medium uppercase tracking-wider"
            style={{
              gridTemplateColumns: "2fr 1.5fr 120px 80px",
              color: "var(--text-muted)",
              borderBottom: "1px solid var(--border-subtle)",
              background: "var(--bg-secondary)",
            }}
          >
            <span>Name</span>
            <span>Target Role</span>
            <span>Updated</span>
            <span className="text-right">Actions</span>
          </div>

          {/* Table rows */}
          {resumes.map((r, i) => (
            <div
              key={r.id}
              data-testid="resume-card"
              className="row-hover grid items-center px-4 py-2.5 cursor-pointer"
              style={{
                gridTemplateColumns: "2fr 1.5fr 120px 80px",
                borderBottom: i < resumes.length - 1 ? "1px solid var(--border-subtle)" : undefined,
              }}
              role="button"
              tabIndex={0}
              onClick={() => navigate({ to: "/resumes/$resumeId", params: { resumeId: r.id } })}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate({ to: "/resumes/$resumeId", params: { resumeId: r.id } }); } }}
            >
              {/* Name column */}
              <div className="flex items-center gap-2 min-w-0">
                {r.isMaster && (
                  <span style={{ color: "var(--accent)", fontSize: "12px", lineHeight: 1 }} title={m.common_master()}>
                    &#9733;
                  </span>
                )}
                <span
                  className="text-[13px] font-medium"
                  style={{ color: "var(--text)" }}
                >
                  {r.title}
                </span>
              </div>

              {/* Target Role */}
              <span className="text-[13px]" style={{ color: "var(--text-muted)" }}>
                {r.data.work?.[0]?.position || "\u2014"}
              </span>

              {/* Updated */}
              <span className="text-[12px] mono tabular" style={{ color: "var(--text-muted)" }}>
                {formatRelativeTime(r.updatedAt)}
              </span>

              {/* Actions */}
              <div className="flex items-center justify-end gap-1" role="presentation" onClick={(e) => e.stopPropagation()}>
                <Link
                  to="/resumes/$resumeId"
                  params={{ resumeId: r.id }}
                  className="inline-flex items-center justify-center rounded-md p-1.5 transition-colors"
                  style={{ color: "var(--text-muted)" }}
                  title="Edit"
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = "var(--text)"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = "var(--text-muted)"; }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </Link>
                <button
                  onClick={() => setDeleteTarget(r.id)}
                  data-testid="resume-delete-btn"
                  className="inline-flex items-center justify-center rounded-md p-1.5 transition-colors"
                  style={{ color: "var(--text-muted)" }}
                  title={m.common_delete()}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = "var(--error, #ef4444)"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = "var(--text-muted)"; }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M3 6h18" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    <line x1="10" y1="11" x2="10" y2="17" />
                    <line x1="14" y1="11" x2="14" y2="17" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* ── Card view ── */
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3" data-testid="resume-list">
          {resumes.map((r) => (
            <div
              key={r.id}
              data-testid="resume-card"
              className="row-hover rounded-lg p-4 cursor-pointer"
              style={{ border: "1px solid var(--border-subtle)" }}
              role="button"
              tabIndex={0}
              onClick={() => navigate({ to: "/resumes/$resumeId", params: { resumeId: r.id } })}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate({ to: "/resumes/$resumeId", params: { resumeId: r.id } }); } }}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    {r.isMaster && (
                      <span style={{ color: "var(--accent)", fontSize: "12px", lineHeight: 1 }} title={m.common_master()}>
                        &#9733;
                      </span>
                    )}
                    <span
                      className="truncate text-[13px] font-medium"
                      style={{ color: "var(--text)" }}
                    >
                      {r.title}
                    </span>
                  </div>
                  <p className="mt-1 text-[12px] mono tabular" style={{ color: "var(--text-muted)" }}>
                    {formatRelativeTime(r.updatedAt)}
                  </p>
                </div>
                <div className="flex items-center gap-0.5" role="presentation" onClick={(e) => e.stopPropagation()}>
                  <Link
                    to="/resumes/$resumeId"
                    params={{ resumeId: r.id }}
                    className="inline-flex items-center justify-center rounded-md p-1.5 transition-colors"
                    style={{ color: "var(--text-muted)" }}
                    title="Edit"
                    onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = "var(--text)"; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = "var(--text-muted)"; }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </Link>
                  <button
                    onClick={() => setDeleteTarget(r.id)}
                    data-testid="resume-delete-btn"
                    className="inline-flex items-center justify-center rounded-md p-1.5 transition-colors"
                    style={{ color: "var(--text-muted)" }}
                    title={m.common_delete()}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = "var(--error, #ef4444)"; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = "var(--text-muted)"; }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M3 6h18" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      <line x1="10" y1="11" x2="10" y2="17" />
                      <line x1="14" y1="11" x2="14" y2="17" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
        title={m.resumes_confirm_delete()}
        confirmLabel={m.common_delete()}
        cancelLabel={m.cover_letters_cancel()}
        destructive
      />
    </div>
  );
}
